import { ImMNElement } from '../../common/elements/elementModelTypes';
import { ImmutableMap } from '../../common/utils/immutableHelper';
import { getElementChildIdsSelector, getElementChildren } from './selectors/elementChildrenSelector';
import { getElementId } from '../../common/elements/utils/elementPropertyUtils';
import { createShallowSelector } from '../utils/milanoteReselect/milanoteReselect';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const createFilteredElementChildrenSelector = (filterFunction?: (element: ImMNElement) => boolean) =>
    createShallowSelector(getElementChildren(), (childElements) =>
        filterFunction ? childElements.valueSeq().filter(filterFunction) : childElements,
    );

const createFilteredElementChildIdsSelector = (filterFunction?: (element: ImMNElement) => boolean) =>
    createShallowSelector(createFilteredElementChildrenSelector(filterFunction), (filteredChildren) =>
        filteredChildren.map(getElementId).toArray(),
    );

const createFilteredChildIdsSelector = (elementId: string, filterFunction?: (element: ImMNElement) => boolean) => {
    const selectChildIds = getElementChildIdsSelector();
    const selectFilteredElementChildIds = createFilteredElementChildIdsSelector(filterFunction);
    const props = { elementId };

    return (state: ImmutableMap): string[] | undefined => {
        const childIds = selectChildIds(state, props);

        if (!childIds || childIds.length === 0 || !filterFunction) return childIds;

        // Only run the children selector if we have child IDs
        return selectFilteredElementChildIds(state, props);
    };
};

export const useFilteredChildIds = (elementId: string, filterFunction?: (element: ImMNElement) => boolean) => {
    const selectFilteredChildIds = useMemo(
        () => createFilteredChildIdsSelector(elementId, filterFunction),
        [filterFunction, elementId],
    );

    return useSelector(selectFilteredChildIds);
};
